import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Row, Col, Card, CardBody, Button, Input, Label, FormGroup,
  Nav, NavItem, NavLink, TabContent, TabPane, Badge, Table, Modal, ModalHeader, ModalBody, ModalFooter, Spinner
} from 'reactstrap';
import classnames from 'classnames';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Flatpickr from "react-flatpickr";
import { toast } from 'react-toastify';
import axios from 'axios';
import BASE_URL from '../../config';
import Header from "../../components/VerticalLayout/Header";
import Footer from "../../components/VerticalLayout/Footer";
import Sidebar from "../../components/VerticalLayout/Sidebar";
import { hasPagePermissionWithAdmin } from '../../components/Permission/PermissionService';
import { useNavigate, useLocation } from 'react-router-dom';

// Styling
import './QuickInvoice.css';

// API Functions
import { searchCustomers } from '../../components/Api/GmsCustomer';
import { getSources } from '../../components/Api/GmsSource';
import { getCampaignsForInvoice, getNextSalesInvoiceNumber, saveSalesInvoice, getCustomerBranchLoyaltyBalance, getActiveLoyaltyConfig, getInvoiceForPrint } from '../../components/Api/GmsBilling';
import { getBusinesses } from '../../components/Api/GmsBusiness';
import Swal from 'sweetalert2';
import { Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { searchProducts } from '../../components/Api/GmsProduct';
import { searchSpaServices } from '../../components/Api/GmsSpaService';
import { getSpaPackages } from '../../components/Api/GmsSpaPackage';
import { getSpaPackageSeries } from '../../components/Api/GmsSpaPackageSeries';
import { getGiftCards, getGiftCardBalance } from '../../components/Api/GmsGiftCard';
import { getTaxes } from '../../components/Api/GmsTax';
import { checkDayClosing } from '../../components/Api/GmsDayClosing';
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

// Existing Components
import CompanyBranchModal from '../../components/Common/CompanyBranchModal';
// Note: We might need to extract PaymentSettlement as a component or import it if it was a component,
// but in BillingPage it's often inline or part of the same file. I'll implement a simplified version or extract logic.

const InvoicePrintModal = ({ isOpen, toggle, invoiceData, businesses = [] }) => {
  if (!invoiceData || !invoiceData.header) return null;

  const printRef = useRef(null);

  const header = invoiceData.header;
  const details = invoiceData.details || [];
  const taxes = invoiceData.taxes || [];

  // Extract values with fallback for camelCase/PascalCase
  const invoiceNo = header.invoice_No || header.Invoice_No;
  const invoiceDate = header.invoice_Date || header.Invoice_Date;
  const customerName = header.customer_NameEn || header.Customer_NameEn;
  const customerAddress = header.customer_AddressEn || header.Customer_AddressEn;
  const customerTRN = header.customer_TRN || header.Customer_TRN;
  const customerContactNo = header.customer_ContactNo || header.Customer_ContactNo;
  const businessName = header.business_NameEn || header.Business_NameEn;
  const branchName = header.branch_NameEn || header.Branch_NameEn;
  const businessTRN = header.business_TRN || header.Business_TRN;
  const companyLogoPath = header.company_LogoPath || header.Company_LogoPath;
  const invoiceType = header.invoice_TypeEn || header.Invoice_TypeEn || header.invoice_Type || header.Invoice_Type || 'Standard';

  // Get business logo with fallback to company logo
  const invoiceBusinessId = header.invoice_BusinessId || header.Invoice_BusinessId;
  const business = businesses.find(b => parseInt(b.business_Id || b.Business_Id) === parseInt(invoiceBusinessId));
  const businessLogoPath = business?.business_LogoPath || null;
  const businessAddress = business?.business_Address || business?.business_AddressEn || header.business_AddressEn || header.Business_AddressEn || '';
  const logoPath = businessLogoPath || companyLogoPath;
  const cashAmount = header.cashAmount || header.CashAmount || 0;
  const cashlessAmount = header.cashlessAmount || header.CashlessAmount || 0;
  const paidAmount = header.paidAmount || header.PaidAmount || 0;
  const tipAmount = header.tipAmount || header.TipAmount || 0;
  const totalVAT = header.invoice_TaxAmount || header.Invoice_TaxAmount || 0;
  const overallDiscount = header.overallDiscountAmount || header.OverallDiscountAmount || 0;
  const grandTotal = header.invoice_NetAmount || header.Invoice_NetAmount || 0;
  const giftCardAmount = header.giftCardAmount || header.GiftCardAmount || 0;
  const pointsAmount = header.pointsAmount || header.PointsAmount || 0;
  const paymentMethod = header.paymentMethod || header.PaymentMethod || '';

  const formatAmount = (amount) => {
    return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getCashlessLabel = (method) => {
    switch (method) {
      case 'cc': return 'Credit Card Amount';
      case 'ba': return 'Bank Transfer Amount';
      case 'on': return 'Online Payment Amount';
      case 'ta': return 'Tabby Amount';
      default: return 'Cashless Amount';
    }
  };
  const cashlessLabel = getCashlessLabel(paymentMethod);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getInvoiceTypeLabel = (type) => {
    if (!type) return 'N/A';
    const lowType = type.toString().toLowerCase();
    if (lowType === 'ca') return 'Cash';
    if (lowType === 'cr') return 'Credit';
    return type;
  };

  const invoiceTypeLabel = getInvoiceTypeLabel(header.invoice_TypeEn || header.Invoice_TypeEn || header.invoice_Type || header.Invoice_Type);

  const handlePrint = () => {
    if (!printRef.current) return;
    
    const element = printRef.current;
    const opt = {
      margin:       [10, 10, 10, 10],
      filename:     `Invoice - INV-${invoiceNo}${Date.now()}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true, logging: false },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak:    { mode: ['css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save();
  };

  const ITEMS_PER_PAGE = 8;
  const pagesCount = Math.max(1, Math.ceil((details.length || 0) / ITEMS_PER_PAGE));
  
  const renderHeader = (hideBorderBottom = false) => (
      <React.Fragment>
          <tr>
            <th colSpan="8" style={{ border: 'none', padding: '0 0 20px 0' }}>
              <div className="invoice-header-bg">
                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                  <h3 style={{ fontSize: '24px', margin: '0', color: 'black', fontWeight: 'bold', letterSpacing: '1px' }}>TAX INVOICE</h3>
                </div>
                 <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr>
                      {logoPath && (
                        <td style={{ width: '120px', border: 'none', padding: '0 20px 0 0', verticalAlign: 'middle' }}>
                          <img
                            src={`${BASE_URL}${logoPath.startsWith('/') ? logoPath : '/' + logoPath}`}
                            alt="Business Logo"
                            style={{ maxHeight: '90px', maxWidth: '110px', objectFit: 'contain' }}
                            crossOrigin="anonymous"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </td>
                      )}
                      <td style={{ border: 'none', verticalAlign: 'middle', textAlign: 'left' }}>
                        <h4 className="mb-1" style={{ fontSize: '20px', color: 'black', fontWeight: '800' }}>{(businessName || 'COMPANY').toUpperCase()}</h4>
                        {businessAddress && <p className="mb-2" style={{ color: 'black', fontSize: '13px', fontWeight: '600', lineHeight: '1.4', maxWidth: '500px' }}>{businessAddress}</p>}
                        {businessTRN && businessTRN.trim() !== '' && (
                          <div className="trn-badge">TRN: {businessTRN}</div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </th>
          </tr>
          <tr>
            <th colSpan="8" style={{ border: 'none', padding: '0 0 20px 0' }}>
              <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ width: '50%', border: 'none', padding: '0 8px', verticalAlign: 'top', textAlign: 'left' }}>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Date:</strong> {formatDate(invoiceDate)}</p>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice No:</strong> {(header.isReturn || header.IsReturn) ? '' : 'INV-'}{invoiceNo}</p>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Invoice Type:</strong> {invoiceTypeLabel}</p>
                    </td>
                    <td style={{ width: '50%', border: 'none', padding: '0 8px', verticalAlign: 'top', textAlign: 'left' }}>
                      <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Customer Name:</strong> {customerName || 'N/A'}{customerContactNo && customerContactNo.trim() !== '' && ` (${customerContactNo})`}</p>
                      {customerAddress && customerAddress.trim() !== '' && (
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Address:</strong> {customerAddress}</p>
                      )}
                      {customerTRN && customerTRN.trim() !== '' && (
                        <p className="mb-1" style={{color: 'black', fontSize: '13px'}}><strong>Customer TRN:</strong> {customerTRN}</p>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </th>
          </tr>
          <tr className="invoice-table-header-row">
            <th style={{color: 'black'}}>S.No / الرقم</th>
            <th style={{color: 'black'}}>Description / الوصف</th>
            <th className="text-end" style={{color: 'black'}}>Price / السعر</th>
            <th className="text-end" style={{color: 'black'}}>Qty / الكمية</th>
            <th className="text-end" style={{color: 'black'}}>Disc. / الخصم</th>
            <th className="text-end" style={{color: 'black'}}>Taxable Amount / المبلغ الخاضع للضريبة</th>
            <th className="text-end" style={{color: 'black'}}>VAT / ضريبة القيمة المضافة</th>
            <th className="text-end" style={{color: 'black'}}>Total / الإجمالي</th>
          </tr>
      </React.Fragment>
  );

  const renderTotals = () => (
      <React.Fragment>
          <tr>
            <td colSpan="6" rowSpan={2 + (taxes?.length || 0) + (overallDiscount > 0 ? 1 : 0) + (tipAmount > 0 ? 1 : 0) + (pointsAmount > 0 ? 1 : 0) + (giftCardAmount > 0 ? 1 : 0)} style={{ border: 'none' }}></td>
            <td className="text-end" style={{color: 'black'}}><strong>Subtotal (AED):</strong></td>
            <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(details.reduce((sum, item) => sum + ((item.netAmount || item.NetAmount || 0) - (item.taxAmt || item.TaxAmt || 0)), 0))}</strong></td>
          </tr>
          {taxes && taxes.length > 0 && (
            (() => {
              const taxTotals = {};
              taxes.forEach(tax => {
                const name = tax.tax_NameEn || tax.Tax_NameEn || 'Tax';
                const perc = tax.tax_Perc || tax.Tax_Perc || 5;
                const key = `${name}_${perc}`;
                if (!taxTotals[key]) taxTotals[key] = { name, perc, amount: 0 };
              });
              details.forEach(item => {
                const itemTax = item.taxAmt || item.TaxAmt || 0;
                const firstTaxKey = Object.keys(taxTotals)[0];
                if (firstTaxKey) taxTotals[firstTaxKey].amount += itemTax;
              });
              return Object.values(taxTotals).map(tax => (
                <tr key={`${tax.name}_${tax.perc}`}>
                  <td className="text-end" style={{color: 'black'}}><strong>{tax.name} ({tax.perc}%) (AED):</strong></td>
                  <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(tax.amount)}</strong></td>
                </tr>
              ));
            })()
          )}
          {overallDiscount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Discount (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(overallDiscount)}</strong></td>
            </tr>
          )}
          {tipAmount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Tip (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(tipAmount)}</strong></td>
            </tr>
          )}
          {pointsAmount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Loyalty (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>- {formatAmount(pointsAmount)}</strong></td>
            </tr>
          )}
          {giftCardAmount > 0 && (
            <tr>
              <td className="text-end" style={{color: 'black'}}><strong>Gift Card (AED):</strong></td>
              <td className="text-end" style={{color: 'black'}}><strong>- {formatAmount(giftCardAmount)}</strong></td>
            </tr>
          )}
          <tr style={{ backgroundColor: '#ebf2f8' }}>
            <td className="text-end" style={{color: 'black'}}><strong>Grand Total (AED):</strong></td>
            <td className="text-end" style={{color: 'black'}}><strong>{formatAmount(grandTotal)}</strong></td>
          </tr>
      </React.Fragment>
  );

  const renderTerms = () => (
    <tfoot>
      <tr>
        <td colSpan="8" style={{ border: 'none', padding: 0 }}>
          <div className="row mt-4 mb-4" style={{ pageBreakInside: 'avoid' }}>
            <div className="col-12">
              <div className="border border-secondary rounded p-3" style={{ backgroundColor: '#fdfdfd' }}>
                <h6 style={{ fontSize: '14px', fontWeight: 'bold', color: '#5d4037' }}>Terms & Notes / الشروط والملاحظات:</h6>
                <ul className="ps-3 mb-0" style={{ fontSize: '12px', listStyleType: 'disc', color: 'black' }}>
                  <li>Discounts are applied per service as shown / يتم تطبيق الخصومات لكل خدمة كما هو موضح</li>
                  <li>VAT charged as per UAE regulations / يتم احتساب ضريبة القيمة المضافة حسب لوائح الإمارات العربية المتحدة</li>
                  <li>No refund after service completion / لا يوجد استرداد بعد اكتمال الخدمة</li>
                  <li>This is a computer-generated invoice and does not require a signature / هذه فاتورة تم إنشاؤها بواسطة الكمبيوتر ولا تتطلب توقيعًا</li>
                </ul>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </tfoot>
  );

  let currentItemIndex = 0;
  const pagesList = [];
  
  for (let i = 0; i < pagesCount; i++) {
      const isLastRowPage = (i === pagesCount - 1);
      const pageItems = details.slice(i * ITEMS_PER_PAGE, (i + 1) * ITEMS_PER_PAGE);
      const requiresExtraTotalsPage = isLastRowPage && pageItems.length > 5;
      
      const showTotalsAndTermsOnThisPage = isLastRowPage && !requiresExtraTotalsPage;
      
      pagesList.push(
          <div key={`page-${i}`} style={{ pageBreakAfter: (showTotalsAndTermsOnThisPage && !requiresExtraTotalsPage) ? 'auto' : 'always', marginBottom: '20px' }}>
              <table className="table invoice-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'black' }}>
                  <thead>{renderHeader()}</thead>
                  <tbody>
                      {pageItems.map((detail, index) => {
                           const priceWithTax = detail.netAmount || detail.NetAmount || 0;
                           const taxAmt = detail.taxAmt || detail.TaxAmt || 0;
                           const taxableAmount = priceWithTax - taxAmt;
                           const originalPrice = detail.price || detail.Price || 0;
                           
                           return (
                              <tr key={index}>
                                <td style={{color: 'black'}}>{currentItemIndex + index + 1}</td>
                                <td style={{color: 'black'}}>
                                  {detail.entityName || detail.EntityName}
                                  {(detail.entityType === 'gif' || detail.EntityType === 'gif') && detail.stockData && ` (${detail.stockData})`}
                                </td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(originalPrice)}</td>
                                <td className="text-end" style={{color: 'black'}}>{Math.round(detail.quantity || detail.Quantity || 0)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(detail.disAmount || detail.DisAmount || 0)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(taxableAmount)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(taxAmt)}</td>
                                <td className="text-end" style={{color: 'black'}}>{formatAmount(priceWithTax)}</td>
                              </tr>
                           );
                      })}
                      {showTotalsAndTermsOnThisPage && renderTotals()}
                  </tbody>
                  {showTotalsAndTermsOnThisPage && renderTerms()}
              </table>
          </div>
      );
      currentItemIndex += ITEMS_PER_PAGE;
      
      if (requiresExtraTotalsPage) {
           pagesList.push(
              <div key={`page-totals`} style={{ pageBreakAfter: 'auto' }}>
                  <table className="table invoice-table" style={{ width: '100%', borderCollapse: 'collapse', color: 'black' }}>
                      <thead>{renderHeader(true)}</thead>
                      <tbody>
                          <tr><td colSpan="8" style={{ border: 'none', padding: '10px 0' }}></td></tr>
                          {renderTotals()}
                      </tbody>
                      {renderTerms()}
                  </table>
              </div>
          );
      }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <style>
        {`
          .invoice-print-content, 
          .invoice-print-content * {
            color: black !important;
            font-family: 'Inter', sans-serif !important;
          }
          .invoice-header-bg {
            background-color: #ebf2f8 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 25px;
            border: 1px solid #d6e4f0;
          }
          .trn-badge {
            background-color: #d6e4f0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            padding: 4px 15px;
            border-radius: 6px;
            font-weight: bold;
            display: inline-block;
            border: 1px solid #c1d5e6;
            color: #2c3e50;
            font-size: 13px;
          }
          .invoice-table {
            margin-bottom: 0 !important;
            border-collapse: collapse !important;
            border: none !important;
          }
          .invoice-table th {
            background-color: #f0f5fa !important;
            border: 1px solid #dee2e6 !important;
            padding: 12px 10px !important;
            font-weight: 700 !important;
            font-size: 13px !important;
            text-align: left;
          }
          .invoice-table-header-row th:first-child {
            border-top-left-radius: 12px !important;
          }
          .invoice-table-header-row th:last-child {
            border-top-right-radius: 12px !important;
          }
          .invoice-table td {
            border: 1px solid #dee2e6 !important;
            padding: 10px !important;
            font-size: 13px !important;
          }
          .invoice-table .text-end {
            text-align: right !important;
          }
        `}
      </style>
      <ModalHeader toggle={toggle}>
        Invoice - {(header.isReturn || header.IsReturn) ? '' : 'INV-'}{header.invoice_No || header.Invoice_No}
      </ModalHeader>
      <ModalBody>
        <div ref={printRef} className="p-4 invoice-print-content" style={{ color: 'black' }}>
          {pagesList}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handlePrint} className="waves-effect waves-light d-flex align-items-center">
          <Printer size={16} className="me-2" />
          Print
        </Button>
        <Button color="secondary" onClick={toggle} className="waves-effect waves-light">
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const QuickInvoicePage = () => {
   const navigate = useNavigate();
   const location = useLocation();
   const isFromBookingRef = useRef(false);
  const [permissions, setPermissions] = useState(JSON.parse(localStorage.getItem('permissions') || '[]'));
  const [activeTab, setActiveTab] = useState('ser'); // Default to Services
  
  const entityMeta = {
    ser: { label: 'SERVICES', icon: 'fas fa-spa', color: 'info' },
    pro: { label: 'PRODUCTS', icon: 'fas fa-box', color: 'primary' },
    pac: { label: 'PACKAGES', icon: 'fas fa-gift', color: 'warning' },
    sep: { label: 'PACKAGE SERIES', icon: 'fas fa-layer-group', color: 'secondary' },
    gif: { label: 'GIFT CARD', icon: 'fas fa-id-card-alt', color: 'danger' }
  };

  const [qtyInputValues, setQtyInputValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isReturnMode, setIsReturnMode] = useState(false);
  
  // Auth & Session
  const [authUser, setAuthUser] = useState(JSON.parse(localStorage.getItem('authUser') || '{}'));
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Print States
  const [showInvoicePrintModal, setShowInvoicePrintModal] = useState(false);
  const [invoicePrintData, setInvoicePrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);

  // Form State
  const [form, setForm] = useState({
    Invoice_Date: new Date(),
    Invoice_CustomerId: null,
    Invoice_SourceId: null,
    Invoice_CampaignId: null,
    Invoice_Type: 'ca', 
    Invoice_No: 'Draft',
    entityItems: [],
    Invoice_TotalAmount: 0,
    Invoice_TaxAmount: 0,
    Invoice_NetAmount: 0,
    Invoice_TotalQty: 0,
    PaymentMethod: 'ca', // Default to Cash
    CashAmount: 0,
    CashlessAmount: 0,
    GiftCardCode: '',
    GiftCardAmount: 0,
    PointsRedeemed: 0,
    PointsAmount: 0,
  });

  const [redeemLoading, setRedeemLoading] = useState(false);
  const [activeRedemption, setActiveRedemption] = useState(null); // 'loyalty' or 'gift'
  const [activeLoyaltyConfig, setActiveLoyaltyConfig] = useState(null);
  const [customerBranchLoyaltyBalance, setCustomerBranchLoyaltyBalance] = useState(0);
  const [giftCardBalance, setGiftCardBalance] = useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [allTaxes, setAllTaxes] = useState([]);

  // Data for Right Panel
  const [topSoldItems, setTopSoldItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Lists for Dropdowns
  const [sourceOptions, setSourceOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  // Sidebar Collapse Logic
  useEffect(() => {
    // Only collapse if sidebar-enable is present
    const isSidebarCollapsed = document.body.classList.contains("vertical-collpsed");
    if (!isSidebarCollapsed) {
       document.body.classList.add("vertical-collpsed");
       document.body.classList.add("sidebar-enable");
    }

    return () => {
      // Re-expand on leave
      document.body.classList.remove("vertical-collpsed");
      document.body.classList.remove("sidebar-enable");
    };
  }, []);

  useEffect(() => {
    if (!selectedBusiness) {
       setShowCompanyModal(true);
    }
  }, []);

   // Fetch Options
   useEffect(() => {
      if (selectedBusiness && selectedBusiness.value) {
         fetchSources();
         fetchNextInvoiceNumber();
         fetchTaxes();
         fetchConfigs();
         fetchBusinessesData();
         fetchTopSoldItems();
         fetchLoyaltyConfig();
      }
   }, [selectedBusiness, selectedBranch]);

   const fetchBusinessesData = async () => {
      try {
         const data = await getBusinesses();
         setBusinesses(data || []);
      } catch (e) { console.error(e); }
   };

   const fetchConfigs = async () => {
      if (selectedBusiness && form.Invoice_Date) {
         fetchCampaigns();
      }
   };

   useEffect(() => {
      if (selectedBusiness && form.Invoice_Date) {
         fetchCampaigns();
      }
   }, [selectedBusiness, form.Invoice_Date, form.Invoice_SourceId]);

   useEffect(() => {
      if (selectedBusiness && selectedBranch && form.Invoice_CustomerId) {
         fetchCustomerLoyalty();
      }
   }, [selectedBusiness, selectedBranch, form.Invoice_CustomerId]);

   const fetchLoyaltyConfig = async () => {
      try {
         const config = await getActiveLoyaltyConfig(selectedBusiness.value, selectedBranch.value, new Date());
         setActiveLoyaltyConfig(config);
      } catch (e) { console.error("Error fetching loyalty config", e); }
   };

   const fetchCustomerLoyalty = async () => {
     try {
        const balance = await getCustomerBranchLoyaltyBalance(form.Invoice_CustomerId, selectedBranch.value, selectedBusiness.value);
        setCustomerBranchLoyaltyBalance(balance);
     } catch (e) { console.error("Error fetching loyalty balance", e); }
   };

   const fetchCampaigns = async () => {
      try {
         const data = await getCampaignsForInvoice(selectedBusiness.value, form.Invoice_Date, form.Invoice_SourceId);
         setCampaignOptions(data.map(c => ({ value: c.campaignId || c.CampaignId, label: c.campaignName || c.CampaignName })));
      } catch (e) { console.error("Error fetching campaigns", e); }
   };

  const fetchTaxes = async () => {
     try {
        const data = await getTaxes();
        setAllTaxes(data.filter(t => t.tax_Active));
     } catch (e) { toast.error("Failed to load taxes"); }
  };

  const fetchSources = async () => {
    try {
      const data = await getSources({ PageSize: 100 });
      const sources = data.sources || data.Sources || [];
      setSourceOptions(sources.map(s => ({ 
        value: s.source_Id || s.Source_Id, 
        label: s.sourceName || s.SourceName 
      })));
    } catch (e) { 
      toast.error("Failed to load sources"); 
    }
  };

  const fetchNextInvoiceNumber = async () => {
    if (!selectedBusiness || !selectedBusiness.value) return;
    try {
      const data = await getNextSalesInvoiceNumber(parseInt(selectedBusiness.value));
      setForm(prev => ({ ...prev, Invoice_No: data.nextInvoiceNumber || 'Draft' }));
    } catch (e) {
      console.error(e);
    }
  };

  const onCustomerSearch = async (inputValue) => {
     if (inputValue.length < 3) return [];
     try {
        const data = await searchCustomers(inputValue);
        return data.items.map(c => ({
           value: c.customer_Id,
           label: `${c.customer_NameEn} (${c.customer_ContactNo})`,
           customer: c
        }));
     } catch (e) { return []; }
  };

  const handleCustomerChange = (selected) => {
     if (!selected) {
       setSelectedCustomer(null);
       setForm(prev => ({ ...prev, Invoice_CustomerId: null, Invoice_SourceId: null }));
       return;
    }
     const customer = selected.customer;
     setSelectedCustomer(selected);
    setForm(prev => ({
       ...prev,
       Invoice_CustomerId: selected.value,
       Invoice_SourceId: customer.customer_LastSourceId || customer.Customer_LastSourceId || null
    }));
  };

  const calculateTotals = (items) => {
     let totalAmount = 0;
     let taxAmount = 0;
     let totalQty = 0;

     items.forEach(item => {
        totalAmount += item.price * item.quantity;
        totalQty += item.quantity;
        // Simplified tax calculation for now - in BillingPage it's per item
        // I'll implement a basic version that matches their tax structure
        if (item.taxAmt) taxAmount += item.taxAmt;
     });

     const netAmount = totalAmount + taxAmount;
     setForm(prev => ({ 
        ...prev, 
        entityItems: items,
        Invoice_TotalAmount: totalAmount,
        Invoice_TaxAmount: taxAmount,
        Invoice_NetAmount: netAmount,
        Invoice_TotalQty: totalQty
     }));
  };

  const addItemToInvoice = (item) => {
     const existingIndex = form.entityItems.findIndex(i => i.entityId === item.entityId && i.entityType === item.entityType);
     let newItems = [...form.entityItems];

     if (existingIndex > -1) {
        newItems[existingIndex].quantity += 1;
        // Update item net amount based on new qty
        const base = newItems[existingIndex].price * newItems[existingIndex].quantity;
        newItems[existingIndex].netAmount = base + (newItems[existingIndex].taxAmt || 0);
     } else {
        // Calculate tax for new item (Default VAT or specific taxes)
        // For simplicity, I'll use 0 for now and expand as needed
        const newItem = {
           entityId: item.entityId,
           entityType: item.entityType,
           entityName: item.entityName,
           price: item.price,
           quantity: 1,
           taxAmt: 0, 
           netAmount: item.price
        };
        newItems.push(newItem);
     }
     calculateTotals(newItems);
  };

  const removeItemFromInvoice = (index) => {
     const newItems = form.entityItems.filter((_, i) => i !== index);
     calculateTotals(newItems);
  };

  const updateItemQty = (index, qty) => {
     if (qty < 1) return;
     const newItems = [...form.entityItems];
     newItems[index].quantity = qty;
     newItems[index].netAmount = newItems[index].price * qty + (newItems[index].taxAmt || 0);
     calculateTotals(newItems);
  };

  // Initial Permission & Company Check
  useEffect(() => {
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsQuickInvoice');
    if (!hasPermission) {
      navigate('/Dashboard');
      return;
    }

    // For Admins/SuperAdmins, we always want to show the selection unless they've already picked one in this module
    // Check if company/branch is already set in session and has correctly formatted ID
    const sessionBusiness = sessionStorage.getItem('selectedBusiness');
    const sessionBranch = sessionStorage.getItem('selectedBranch');

    if (sessionBusiness && sessionBranch) {
      const biz = JSON.parse(sessionBusiness);
      const br = JSON.parse(sessionBranch);
      
      // Normalize if from another module with different format
      const normalizedBiz = biz.value ? biz : { value: biz.business_Id || biz.businessId, label: biz.business_NameEn || biz.businessName };
      const normalizedBr = br.value ? br : { value: br.branch_Id || br.branchId, label: br.branch_NameEn || br.branchName };

      if (normalizedBiz.value && normalizedBr.value) {
         setSelectedBusiness(normalizedBiz);
         setSelectedBranch(normalizedBr);
      } else {
         setShowCompanyModal(true);
      }
    } else if (authUser.role !== 'Admin' && authUser.role !== 'SuperAdmin' && authUser.businessId && authUser.branchId) {
      // Auto-select for regular employees
      setSelectedBusiness({ value: authUser.businessId, label: authUser.businessName });
      setSelectedBranch({ value: authUser.branchId, label: authUser.branchName });
    } else {
      setShowCompanyModal(true);
    }
  }, [permissions, navigate, authUser]);

  const handleCompanySelect = (business, branch) => {
    const biz = business.value ? business : { value: business.business_Id, label: business.business_NameEn };
    const br = branch.value ? branch : { value: branch.branch_Id, label: branch.branch_NameEn };
    
    setSelectedBusiness(biz);
    setSelectedBranch(br);
    sessionStorage.setItem('selectedBusiness', JSON.stringify(biz));
    sessionStorage.setItem('selectedBranch', JSON.stringify(br));
    setShowCompanyModal(false);
  };


  // Search Logic
  useEffect(() => {
     const delayDebounceFn = setTimeout(() => {
        if (searchQuery.length >= 3) {
           performSearch();
        } else {
           setSearchResults([]);
        }
     }, 300);

     return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, activeTab]);

  const performSearch = async () => {
     setIsSearching(true);
     try {
        let items = [];
        if (activeTab === 'pro') {
           const result = await searchProducts(searchQuery);
           items = result.items || [];
        } else if (activeTab === 'ser') {
           const result = await searchSpaServices(searchQuery);
           items = result.items || [];
        } else if (activeTab === 'pac') {
           items = await getSpaPackages({ PackageName_En: searchQuery, Status: true });
        } else if (activeTab === 'sep') {
           items = await getSpaPackageSeries({ SeriesName_En: searchQuery, Status: true });
        } else if (activeTab === 'gif') {
           const configs = await getGiftCards({ Status: true });
           items = configs.filter(c => (c.card_Name || "").toLowerCase().includes(searchQuery.toLowerCase()));
        }

        // Map search results to standard item format (using Net Amount as the base for billing)
        const mapped = items.map(item => ({
           entityId: item.product_Id || item.spaService_Id || item.packageId || item.seriesId || item.card_Id || item.Card_Id || item.PackageId || item.SeriesId,
           entityType: activeTab,
           entityName: item.product_NameEn || item.spaServiceName_En || item.packageName_En || item.seriesName_En || item.card_Name || item.PackageName_En || item.SeriesName_En || item.Card_Name,
           price: item.product_NetAmount || item.netAmount || item.amount || item.Product_NetAmount || item.Amount || 0
        }));
        setSearchResults(mapped);
     } catch (e) { console.error(e); }
     setIsSearching(false);
  };

  const fetchTopSoldItems = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/SalesInvoice/get-top-sold-items`, {
        BusinessId: selectedBusiness?.value,
        BranchId: selectedBranch?.value
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTopSoldItems(response.data);
    } catch (error) {
      console.error("Error fetching top sold items", error);
    }
  };

  const validateForm = () => {
     if (!selectedBusiness || !selectedBranch) {
        toast.error("Please select Company and Branch");
        return false;
     }
     if (form.entityItems.length === 0) {
        toast.error("Please add at least one item");
        return false;
     }
     if (!form.Invoice_CustomerId) {
        toast.error("Please select a customer");
        return false;
     }
     return true;
  };

  const handleSaveInvoice = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
       const userId = authUser.employeeId || authUser.id || 1;
       const nowISO = new Date().toISOString();

       // Map items to API format for invoiceDetailsJson
       const invoiceDetails = form.entityItems.map((item, idx) => {
          const tempId = idx + 1;
          const net = item.netAmount || (item.price * item.quantity);
          const taxRate = 0.05; // 5% default
          const basePrice = net / (1 + taxRate);
          const taxAmt = net - basePrice;

          return {
             Detail_Id: null,
             TempDetailId: tempId,
             EntityId: item.entityId,
             EntityType: item.entityType,
             Quantity: item.quantity,
             Price: parseFloat(basePrice.toFixed(2)),
             DisPer: 0,
             DisAmount: 0,
             TaxAmt: parseFloat(taxAmt.toFixed(2)),
             NetAmount: parseFloat(net.toFixed(2)),
             Cost: 0,
             M_InstallmentId: null,
             BookingResourceDetail_Id: null,
             Salesman_ID: 0,
             SalesmanIds: [],
             StockData: null,
             CreatedBy: userId,
             CreatedOn: nowISO
          };
       });

       // Map taxes for taxesJson
       const taxesData = invoiceDetails.map(detail => ({
          Tax_Id: 1, // Default VAT
          TaxPer: 5,
          TempDetailId: detail.TempDetailId,
          CreatedBy: userId,
          CreatedOn: nowISO
       }));

       const saveData = {
          Invoice_No: form.Invoice_No === 'Draft' ? "" : form.Invoice_No,
          Invoice_Date: formatDateForAPI(form.Invoice_Date),
          Invoice_CustomerId: form.Invoice_CustomerId,
          Invoice_BusinessId: selectedBusiness.value,
          Invoice_BranchId: selectedBranch.value,
          Invoice_TotalQty: form.Invoice_TotalQty || form.entityItems.reduce((acc, item) => acc + item.quantity, 0),
          Invoice_TotalAmount: parseFloat(invoiceDetails.reduce((acc, d) => acc + (d.Price * d.Quantity), 0).toFixed(2)),
          Invoice_DisAmount: 0,
          Invoice_TaxAmount: parseFloat(invoiceDetails.reduce((acc, d) => acc + d.TaxAmt, 0).toFixed(2)),
          Invoice_NetAmount: form.Invoice_NetAmount,
          Invoice_Type: form.Invoice_Type || 'ca',
          PaidAmount: form.Invoice_NetAmount,
          CashAmount: form.CashAmount,
          CashlessAmount: form.CashlessAmount,
          PaymentMethod: form.PaymentMethod || 'ca',
          AuthCode: "",
          TipAmount: 0,
          CostAmount: 0,
          TotalCost: 0,
          OverallDiscountPercentage: 0,
          OverallDiscountAmount: 0,
          CreatedBy: userId,
          CreatedDate: formatDateForAPI(new Date()),
          Details: invoiceDetails,
          Taxes: taxesData,
          IsReturn: false,
          PointsRedeemed: form.PointsRedeemed || 0,
          PointsAmount: form.PointsAmount || 0,
          LoyaltyId: 1, 
          PointsAdded: form.Invoice_NetAmount,
          Invoice_SourceId: form.Invoice_SourceId,
          GiftCardAmount: form.GiftCardAmount || 0,
          RedeemedGiftCardCode: form.GiftCardCode || ""
       };

       // Convert to FormData as expected by [FromForm] in SalesInvoiceController
       const formData = new FormData();
       Object.keys(saveData).forEach(key => {
          if (key === 'Details' || key === 'Taxes') {
             // Map to the specific JSON string parameters the controller expects
             formData.append(key === 'Details' ? 'invoiceDetailsJson' : 'taxesJson', JSON.stringify(saveData[key]));
          } else if (saveData[key] !== null && saveData[key] !== undefined) {
             formData.append(key, saveData[key]);
          }
       });

       console.log("Quick Invoice Save FormData Payload:", saveData);

       const result = await saveSalesInvoice(formData);
       if (result.isSuccess || result.IsSuccess || result.success) {
          const returnedId = result.returnedId || result.ReturnedId;
          
          Swal.fire({
             title: "Success",
             text: "Invoice saved successfully!",
             icon: "success",
             showCancelButton: true,
             confirmButtonText: "Print Invoice",
             cancelButtonText: "Close",
             confirmButtonColor: "#556ee6",
             cancelButtonColor: "#6c757d"
          }).then(async (res) => {
             if (res.isConfirmed && returnedId) {
                try {
                   setPrintLoading(true);
                   const printData = await getInvoiceForPrint(returnedId);
                   if (printData && printData.header) {
                      setInvoicePrintData(printData);
                      setShowInvoicePrintModal(true);
                   }
                } catch (e) { console.error("Error loading print data", e); }
                setPrintLoading(false);
             } else if (returnedId) {
                // If user doesn't want to print, still check if they want to book services
                const hasServices = form.entityItems.some(item => 
                   ['ser', 'pac', 'sep'].includes(item.entityType)
                );
                
                if (hasServices) {
                   try {
                      const printData = await getInvoiceForPrint(returnedId);
                      if (printData && printData.header) {
                         // Extract services for the booking prompt
                         const invoiceDetails = printData.details || [];
                         const servicesFromInvoice = invoiceDetails.filter(detail => {
                            const type = (detail.entityType || detail.EntityType || detail.Entity_Type || detail.itemType || detail.ItemType || '').toLowerCase();
                            return type === 'ser' || type === 'pac' || type === 'sep' || type === 'service' || type === 'package';
                         });

                         if (servicesFromInvoice.length > 0) {
                            const customerId = printData.header.invoice_CustomerId || printData.header.Invoice_CustomerId;
                            
                            Swal.fire({
                               title: 'Proceed to Booking?',
                               text: 'Would you like to go to the booking page to book the services from this invoice?',
                               icon: 'question',
                               showCancelButton: true,
                               confirmButtonColor: '#556ee6',
                               cancelButtonColor: '#6c757d',
                               confirmButtonText: 'Yes, Go to Booking',
                               cancelButtonText: 'No, Stay Here'
                            }).then((result) => {
                               if (result.isConfirmed && customerId) {
                                  navigate('/GmsAddBooking', {
                                     state: {
                                        fromInvoice: true,
                                        customerId: customerId,
                                        businessId: printData.header.invoice_BusinessId || printData.header.Invoice_BusinessId,
                                        branchId: printData.header.invoice_BranchId || printData.header.Invoice_BranchId,
                                        sourceId: printData.header.invoice_SourceId || printData.header.Invoice_SourceId,
                                        invoiceId: printData.header.invoice_Id || printData.header.Invoice_Id,
                                        invoiceDetails: servicesFromInvoice.map(detail => ({
                                           detailId: detail.detail_Id || detail.Detail_Id,
                                           entityId: detail.entityId || detail.EntityId,
                                           entityType: detail.entityType || detail.EntityType || detail.Entity_Type || 'ser',
                                           quantity: detail.quantity || detail.Quantity,
                                           serviceId: (detail.entityType || detail.EntityType || detail.Entity_Type || '').toLowerCase().startsWith('ser')
                                              ? (detail.entityId || detail.EntityId)
                                              : null
                                        }))
                                     }
                                  });
                               }
                            });
                         }
                      }
                   } catch (e) { console.error("Error checked for services after save", e); }
                }
             }
          });

          setShowPaymentModal(false);
          setSelectedCustomer(null);
          setForm({
             ...form,
             Invoice_CustomerId: null,
             Invoice_SourceId: null,
             Invoice_CampaignId: null,
             entityItems: [],
             Invoice_TotalAmount: 0,
             Invoice_TaxAmount: 0,
             Invoice_NetAmount: 0,
             Invoice_TotalQty: 0,
             PointsRedeemed: 0,
             PointsAmount: 0,
             GiftCardAmount: 0,
             GiftCardCode: ''
          });
          fetchNextInvoiceNumber();
          fetchTopSoldItems();
          if (typeof fetchLoyaltyConfig === 'function') fetchLoyaltyConfig();
       } else {
          toast.error(result.message || "Failed to save invoice");
       }
    } catch (e) {
       toast.error("An error occurred during save");
       console.error(e);
    }
    setIsSaving(false);
  };

  const handlePrintModalClose = async () => {
    // We use a local reference to ensure we have the data
    const currentPrintData = invoicePrintData;

    if (!currentPrintData || !currentPrintData.header) {
      setShowInvoicePrintModal(false);
      setInvoicePrintData(null);
      return;
    }

    // Extract services from invoice (excluding products)
    const invoiceDetails = currentPrintData.details || [];
    
    const servicesFromInvoice = invoiceDetails.filter(detail => {
      // Check multiple possible property names for entity type (case-insensitive)
      const type = (
        detail.entityType || 
        detail.EntityType || 
        detail.Entity_Type || 
        detail.itemType || 
        detail.ItemType ||
        ''
      ).toLowerCase();
      
      // Include services ('ser'), packages ('pac'), and package series ('sep')
      return type === 'ser' || type === 'pac' || type === 'sep' || type === 'service' || type === 'package';
    });

    // Check if coming from booking page
    // In QuickInvoice, we default to false if not explicitly coming from a booking
    const hasBookingId = location.state?.bookingId || location.state?.BookingId;
    const comingFromBooking = (isFromBookingRef.current && hasBookingId) || false;

    // Only show prompt if there are services AND not already coming from the booking flow
    if (servicesFromInvoice.length > 0 && !comingFromBooking) {
      const customerId = currentPrintData.header.invoice_CustomerId || currentPrintData.header.Invoice_CustomerId;
      const businessId = currentPrintData.header.invoice_BusinessId || currentPrintData.header.Invoice_BusinessId;
      const branchId = currentPrintData.header.invoice_BranchId || currentPrintData.header.Invoice_BranchId;

      Swal.fire({
        title: 'Proceed to Booking?',
        text: 'Would you like to go to the booking page to book the services from this invoice?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#556ee6',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Go to Booking',
        cancelButtonText: 'No, Stay Here'
      }).then((result) => {
        // Close modal first
        setShowInvoicePrintModal(false);

        if (result.isConfirmed && customerId) {
          // Navigate to booking page with invoice data
          navigate('/GmsAddBooking', {
            state: {
              fromInvoice: true,
              customerId: customerId,
              businessId: businessId,
              branchId: branchId,
              sourceId: currentPrintData.header.invoice_SourceId || currentPrintData.header.Invoice_SourceId,
              invoiceId: currentPrintData.header.invoice_Id || currentPrintData.header.Invoice_Id,
              invoiceDetails: servicesFromInvoice.map(detail => ({
                detailId: detail.detail_Id || detail.Detail_Id,
                entityId: detail.entityId || detail.EntityId,
                entityType: detail.entityType || detail.EntityType || detail.Entity_Type || 'ser',
                quantity: detail.quantity || detail.Quantity,
                serviceId: (detail.entityType || detail.EntityType || detail.Entity_Type || '').toLowerCase().startsWith('ser')
                  ? (detail.entityId || detail.EntityId)
                  : null
              }))
            }
          });
        }

        // Clear invoice print data after navigation decision
        setInvoicePrintData(null);
      });
    } else {
      // No services or already coming from booking, just close
      setShowInvoicePrintModal(false);
      setInvoicePrintData(null);
    }
  };

  const handleCheckGiftCard = async () => {
    if (!form.GiftCardCode) return;
    setRedeemLoading(true);
    try {
      const result = await getGiftCardBalance(form.GiftCardCode);
      
      if (result && result.isValid && result.balance > 0) {
        setGiftCardBalance(result.balance);
        Swal.fire("Success", `Gift Card Verified! Available Balance: ${result.balance} AED`, "success");
      } else {
        const errorMsg = result?.message || result?.Message || "Invalid Gift Card or Zero Balance";
        Swal.fire("Invalid Card", errorMsg, "error");
        setGiftCardBalance(null);
      }
    } catch (e) { 
      console.error("Gift Card Error:", e);
      Swal.fire("Error", "Error verifying Gift Card. Please check your connection.", "error");
      setGiftCardBalance(null);
    }
    setRedeemLoading(false);
  };

  const confirmGiftCardRedemption = () => {
    const redeemAmount = parseFloat(form.RedeemGiftCardAmount) || 0;
    if (redeemAmount <= 0) {
       toast.error("Please enter a valid amount");
       return;
    }
    if (redeemAmount > (giftCardBalance || 0)) {
       toast.error("Redemption amount cannot exceed available balance");
       return;
    }

    const maxRedeemable = form.Invoice_NetAmount - (form.PointsAmount || 0);
    const finalAmt = Math.min(redeemAmount, maxRedeemable);

    setForm({
      ...form,
      GiftCardAmount: finalAmt,
      CashAmount: Math.max(0, form.Invoice_NetAmount - finalAmt - (form.PointsAmount || 0)),
      CashlessAmount: 0
    });

    toast.success(`Gift Card Applied: ${finalAmt.toFixed(2)} AED`);
    setActiveRedemption(null);
    setGiftCardBalance(null);
  };

  const handleApplyLoyalty = () => {
    const pts = parseInt(form.PointsRedeemed) || 0;
    if (pts <= 0) {
      toast.error("Please enter points to redeem");
      return;
    }

    const cappedPts = Math.min(pts, customerBranchLoyaltyBalance);
    let amt = 0;
    if (activeLoyaltyConfig) {
      amt = (cappedPts / activeLoyaltyConfig.redeemPoints) * activeLoyaltyConfig.redeemAmount;
    }

    if (amt > 0) {
      const maxRedeemable = form.Invoice_NetAmount - (form.GiftCardAmount || 0);
      const finalAmt = Math.min(amt, maxRedeemable);

      setForm(prev => ({
        ...prev,
        PointsRedeemed: cappedPts,
        PointsAmount: finalAmt,
        CashAmount: Math.max(0, prev.Invoice_NetAmount - finalAmt - (prev.GiftCardAmount || 0)),
        CashlessAmount: 0
      }));
      toast.success(`Loyalty Points Applied! AED ${finalAmt.toFixed(2)}`);
      setActiveRedemption(null);
    } else {
      toast.error("Invalid point amount or loyalty configuration");
    }
  };




  return (
    <React.Fragment>
      <div className="page-content" style={{ paddingTop: '80px' }}>
        <Container fluid className="px-3">
          <Row className="g-2">
            {/* Left Panel - Selected Items & Header */}
            <Col lg={5} xl={4}>
              <Card className="h-100 shadow-sm border-0" style={{ minHeight: 'calc(100vh - 100px)' }}>
                <CardBody className="p-3 d-flex flex-column">
                  {/* Header Fields Section */}
                  <div className="invoice-header mb-3">
                     <Row className="g-2 mb-2">
                        <Col md={12}>
                           <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded border mb-2">
                              <span className="small fw-bold text-muted text-uppercase">Invoice #</span>
                              <span className="fw-bold text-primary">INV-{form.Invoice_No}</span>
                           </div>
                        </Col>
                        <Col md={6}>
                           <FormGroup className="mb-0">
                              <Label className="small mb-1">Date</Label>
                              <Flatpickr
                                 value={form.Invoice_Date}
                                 className="form-control form-control-sm"
                                 onChange={date => setForm({...form, Invoice_Date: date[0]})}
                                 options={{ dateFormat: "d/m/Y" }}
                              />
                           </FormGroup>
                        </Col>
                        <Col md={6}>
                           <FormGroup className="mb-0">
                              <Label className="small mb-1">Invoice Type</Label>
                              <div className="btn-group btn-group-sm d-flex" role="group">
                                 <Button 
                                    color={form.Invoice_Type === 'ca' ? 'primary' : 'light'} 
                                    onClick={() => setForm({...form, Invoice_Type: 'ca'})}
                                    className="flex-fill"
                                 >Cash</Button>
                                 <Button 
                                    color={form.Invoice_Type === 'cr' ? 'danger' : 'light'} 
                                    onClick={() => setForm({...form, Invoice_Type: 'cr'})}
                                    className="flex-fill"
                                 >Credit</Button>
                              </div>
                           </FormGroup>
                        </Col>
                     </Row>
                     <Row className="g-2 mb-2">
                        <Col md={12}>
                           <FormGroup className="mb-0">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                 <Label className="small mb-0">Customer</Label>
                                 <i 
                                    className="bx bx-plus-circle text-primary cursor-pointer" 
                                    title="Add New Customer"
                                    onClick={() => navigate('/GmsCustomer')}
                                  ></i>
                              </div>
                              <AsyncSelect
                                 isClearable
                                 placeholder="Search Customer..."
                                 value={selectedCustomer}
                                 loadOptions={onCustomerSearch}
                                 onChange={handleCustomerChange}
                                 cacheOptions
                                 defaultOptions
                                 styles={{ 
                                    control: (base) => ({ ...base, minHeight: '32px', fontSize: '12px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 })
                                 }}
                              />
                           </FormGroup>
                        </Col>
                     </Row>
                     <Row className="g-2">
                        <Col md={6}>
                           <FormGroup className="mb-0">
                              <Label className="small mb-1">Source</Label>
                              <Select
                                 options={sourceOptions}
                                 value={sourceOptions.find(o => o.value === form.Invoice_SourceId)}
                                 onChange={s => setForm({...form, Invoice_SourceId: s?.value, Invoice_CampaignId: null})}
                                 placeholder="Source"
                                 styles={{ control: (base) => ({ ...base, minHeight: '32px', fontSize: '12px' }) }}
                              />
                           </FormGroup>
                        </Col>
                        <Col md={6}>
                           <FormGroup className="mb-0">
                              <Label className="small mb-1">Campaign</Label>
                              <Select
                                 isClearable
                                 options={campaignOptions}
                                 value={campaignOptions.find(o => o.value === form.Invoice_CampaignId)}
                                 onChange={c => setForm({...form, Invoice_CampaignId: c?.value})}
                                 placeholder="No Campaign"
                                 styles={{ 
                                    control: (base) => ({ ...base, minHeight: '32px', fontSize: '12px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 })
                                 }}
                              />
                           </FormGroup>
                        </Col>
                     </Row>
                  </div>

                  {/* Selected Items List Section */}
                  <div className="flex-grow-1 border rounded p-2 mb-3 bg-light overflow-auto">
                     <Table hover borderless className="mb-0">
                        <thead className="border-bottom">
                           <tr style={{ fontSize: '11px' }}>
                              <th>Item</th>
                              <th style={{ width: '80px' }}>Qty</th>
                              <th className="text-end">Amount</th>
                              <th style={{ width: '40px' }}></th>
                           </tr>
                        </thead>
                        <tbody>
                           {form.entityItems.map((item, index) => (
                              <tr key={`${item.entityType}-${item.entityId}-${index}`} style={{ fontSize: '12px', verticalAlign: 'middle' }}>
                                 <td className="text-truncate" style={{ maxWidth: '120px' }}>
                                    <i className={classnames(entityMeta[item.entityType]?.icon, `text-${entityMeta[item.entityType]?.color}`, 'me-2')}></i>
                                    {item.entityName}
                                 </td>
                                 <td>
                                     <Input 
                                        type="number" 
                                        bsSize="sm" 
                                        min={1}
                                        value={qtyInputValues[index] !== undefined ? qtyInputValues[index] : item.quantity} 
                                        onChange={(e) => setQtyInputValues(prev => ({ ...prev, [index]: e.target.value }))}
                                        onBlur={(e) => {
                                           const parsed = parseInt(e.target.value);
                                           const finalQty = isNaN(parsed) || parsed < 1 ? 1 : parsed;
                                           setQtyInputValues(prev => { const next = { ...prev }; delete next[index]; return next; });
                                           updateItemQty(index, finalQty);
                                        }}
                                        style={{ padding: "2px 5px", height: "25px", textAlign: "center" }}
                                     />
                                 </td>
                                 <td className="text-end">{(item.price * item.quantity).toFixed(2)}</td>
                                 <td className="text-end">
                                    <i 
                                       className="bx bx-trash text-danger cursor-pointer" 
                                       onClick={() => removeItemFromInvoice(index)}
                                    />
                                 </td>
                              </tr>
                           ))}
                           {form.entityItems.length === 0 && (
                              <tr>
                                 <td colSpan="4" className="text-center py-4 text-muted">No items selected</td>
                              </tr>
                           )}
                        </tbody>
                     </Table>
                  </div>

                  {/* Summary & Buttons Section */}
                  <div className="invoice-footer border-top pt-3">
                     <div className="d-flex justify-content-between mb-1 small text-muted">
                        <span>Items: {form.Invoice_TotalQty}</span>
                        <span>Total: {form.Invoice_TotalAmount.toFixed(2)}</span>
                     </div>
                     <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">Net Payable:</span>
                        <span className="fw-bold fs-5 text-primary">{form.Invoice_NetAmount.toFixed(2)} AED</span>
                     </div>
                      <div className="d-grid gap-2 d-md-flex mt-3">
                         <Button 
                            color="secondary" 
                            outline 
                            className="flex-fill py-2 fw-bold" 
                            onClick={() => setForm({...form, entityItems: [], Invoice_TotalAmount: 0, Invoice_TaxAmount: 0, Invoice_NetAmount: 0, Invoice_TotalQty: 0})}
                         >
                            <i className="bx bx-trash me-1"></i> Clear
                         </Button>
                         <Button 
                            color="primary" 
                            className="flex-fill py-2 fw-bold" 
                            disabled={form.entityItems.length === 0}
                            onClick={async () => {
                               const closing = await checkDayClosing({ 
                                  BranchId: selectedBranch.value, 
                                  ClosingDate: form.Invoice_Date 
                               });
                               if (closing.isClosed || closing.isHoliday) {
                                  toast.error(closing.isHoliday ? "Selected date is a holiday" : "Day is already closed for this branch");
                                  return;
                               }
                               setForm(prev => ({ ...prev, CashAmount: prev.Invoice_NetAmount, CashlessAmount: 0 }));
                               setShowPaymentModal(true);
                            }}
                         >
                            <i className="bx bx-check-double me-1"></i> Pay
                         </Button>
                      </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Right Panel - Entity Tabs & Grid */}
            <Col lg={7} xl={8}>
               <Card className="h-100 shadow-sm border-0">
                  <CardBody className="p-3">
                     {/* Search Bar */}
                     <div className="mb-3">
                        <Input 
                           type="text" 
                           placeholder="Search Items (Min 3 chars)..." 
                           value={searchQuery}
                           onChange={(e) => setSearchQuery(e.target.value)}
                           className="shadow-sm border-primary"
                        />
                     </div>

                     {/* Entity Selection Buttons */}
                     <div className="d-flex flex-wrap gap-2 mb-4">
                        {['ser', 'pro', 'pac', 'sep', 'gif'].map(tab => (
                           <Button
                              key={tab}
                              color={entityMeta[tab].color}
                              outline={activeTab !== tab}
                              onClick={() => setActiveTab(tab)}
                              className={classnames("flex-fill d-flex flex-column align-items-center justify-content-center p-3", { "shadow-sm": activeTab === tab })}
                              style={{ 
                                 minWidth: '100px', 
                                 borderRadius: '10px',
                                 borderWidth: '2px',
                                 transition: 'all 0.2s ease',
                                 backgroundColor: activeTab === tab ? undefined : '#fff'
                              }}
                           >
                              <i className={classnames(entityMeta[tab].icon, "mb-2")} style={{ fontSize: '22px' }}></i>
                              <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px' }}>{entityMeta[tab].label}</div>
                           </Button>
                        ))}
                     </div>

                     {/* Content Grid */}
                     <div className="entity-grid" style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
                        {isSearching ? (
                           <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : (
                           <Row className="g-3">
                              {(searchQuery.length >= 3 ? searchResults : topSoldItems.filter(item => item.entityType === activeTab)).map(item => (
                                 <Col key={`${item.entityType}-${item.entityId}`} xs={12} sm={6} md={4} xl={3}>
                                    <Card 
                                       className="h-100 cursor-pointer hover-shadow border bg-light" 
                                       onClick={() => addItemToInvoice(item)}
                                       style={{ transition: 'all 0.2s', borderRadius: '10px' }}
                                    >
                                       <CardBody className="p-3 text-center d-flex flex-column justify-content-center align-items-center">
                                          <div 
                                            className={classnames("mb-2 d-flex align-items-center justify-content-center bg-soft", `text-${entityMeta[item.entityType]?.color}`)} 
                                            style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: `rgba(var(--bs-${entityMeta[item.entityType]?.color}-rgb), 0.1)` }}
                                          >
                                            <i className={entityMeta[item.entityType]?.icon} style={{ fontSize: '18px' }}></i>
                                          </div>
                                          <div className="fw-bold mb-1 text-dark text-truncate w-100" style={{ fontSize: '13px' }}>{item.entityName}</div>
                                          <div className="fw-bold text-primary" style={{ fontSize: '14px' }}>{item.price.toFixed(2)} AED</div>
                                       </CardBody>
                                    </Card>
                                 </Col>
                              ))}
                              {searchQuery.length < 3 && topSoldItems.filter(item => item.entityType === activeTab).length === 0 && (
                                 <Col xs={12} className="text-center py-5 text-muted">No top items found. Try searching.</Col>
                              )}
                              {searchQuery.length >= 3 && searchResults.length === 0 && (
                                 <Col xs={12} className="text-center py-5 text-muted">No results found for "{searchQuery}"</Col>
                              )}
                           </Row>
                        )}
                     </div>
                  </CardBody>
               </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Payment & Settlement Modal */}
      <Modal isOpen={showPaymentModal} toggle={() => setShowPaymentModal(false)} centered size="md">
         <ModalHeader toggle={() => setShowPaymentModal(false)} className="bg-primary text-white">
            Payment & Settlement
         </ModalHeader>
         <ModalBody className="p-4">
            <div className="text-center mb-4">
               <h4 className="text-muted mb-1">Net Amount</h4>
               <h1 className="fw-bold text-primary">{form.Invoice_NetAmount.toFixed(2)} <span className="fs-4">AED</span></h1>
            </div>

            <FormGroup>
               <Label className="fw-bold">Payment Method</Label>
               <div className="d-flex gap-2">
                  {[
                     { id: 'ca', label: 'Cash', icon: 'bx bx-money' },
                     { id: 'cc', label: 'Credit Card', icon: 'bx bx-credit-card' },
                     { id: 'ba', label: 'Bank', icon: 'bx bx-building' },
                     { id: 'ta', label: 'Tabby', icon: 'bx bxs-credit-card-front' }
                  ].map(m => (
                     <Button
                        key={m.id}
                        color={form.PaymentMethod === m.id ? 'primary' : 'outline-secondary'}
                        className="flex-fill p-3"
                        onClick={() => {
                           const cash = m.id === 'ca' ? form.Invoice_NetAmount : 0;
                           const card = m.id !== 'ca' ? form.Invoice_NetAmount : 0;
                           setForm({...form, PaymentMethod: m.id, CashAmount: cash, CashlessAmount: card});
                        }}
                     >
                        <i className={`${m.icon} d-block fs-3 mb-1`}></i>
                        {m.label}
                     </Button>
                  ))}
               </div>
            </FormGroup>

            {form.PaymentMethod !== 'ca' && (
               <FormGroup>
                  <Label>Card/Auth Code (Optional)</Label>
                  <Input type="text" placeholder="Enter last 4 digits or Auth Code" />
               </FormGroup>
            )}

            {/* Redemptions Buttons */}
            {!isReturnMode && (
               <div className="mb-3">
                  <Label className="fw-bold small mb-2 text-muted uppercase">Redeem Balance</Label>
                  <div className="d-flex gap-2">
                     <Button 
                        color={activeRedemption === 'loyalty' ? 'primary' : 'outline-primary'} 
                        className="flex-fill d-flex align-items-center justify-content-center gap-2 py-2"
                        disabled={customerBranchLoyaltyBalance <= 0 || !activeLoyaltyConfig}
                        onClick={() => setActiveRedemption(activeRedemption === 'loyalty' ? null : 'loyalty')}
                     >
                        <i className="bx bx-award fs-5"></i>
                        <span>Loyalty ({customerBranchLoyaltyBalance})</span>
                     </Button>
                     <Button 
                        color={activeRedemption === 'gift' ? 'danger' : 'outline-danger'} 
                        className="flex-fill d-flex align-items-center justify-content-center gap-2 py-2"
                        onClick={() => setActiveRedemption(activeRedemption === 'gift' ? null : 'gift')}
                     >
                        <i className="bx bx-gift fs-5"></i>
                        <span>Gift Card</span>
                     </Button>
                  </div>
               </div>
            )}

            {/* Loyalty Redeemption Section */}
            {activeRedemption === 'loyalty' && (
               <div className="border rounded p-3 mb-3 bg-white shadow-sm border-primary">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                     <Label className="fw-bold mb-0 text-primary">Redeem Loyalty Points</Label>
                     <span className="small text-muted">{customerBranchLoyaltyBalance} Available</span>
                  </div>
                  {activeLoyaltyConfig && (
                     <div className="small text-muted mb-2">
                        Rate: {activeLoyaltyConfig.redeemPoints} pts = AED {activeLoyaltyConfig.redeemAmount.toFixed(2)}
                     </div>
                  )}
                  <div className="d-flex gap-2">
                     <Input 
                        type="number" 
                        placeholder="Points to redeem" 
                        value={form.PointsRedeemed || ''}
                        onChange={e => setForm({...form, PointsRedeemed: e.target.value})}
                     />
                     <Button color="primary" onClick={handleApplyLoyalty}>Redeem</Button>
                  </div>
               </div>
            )}

            {/* Gift Card Redemption Section */}
            {activeRedemption === 'gift' && (
               <div className="border rounded p-3 mb-3 bg-white shadow-sm border-danger">
                  <Label className="fw-bold mb-2 text-danger">Redeem Gift Card</Label>
                  <div className="d-flex gap-2 mb-3">
                     <Input 
                        type="text" 
                        placeholder="Enter Gift Card Code" 
                        value={form.GiftCardCode || ''}
                        onChange={e => {
                          setForm({...form, GiftCardCode: e.target.value});
                          setGiftCardBalance(null); // Reset balance when code changes
                        }}
                     />
                     <Button color="danger" outline onClick={handleCheckGiftCard} disabled={redeemLoading}>
                        {redeemLoading ? <Spinner size="sm" /> : 'Check'}
                     </Button>
                  </div>

                  {giftCardBalance !== null && (
                    <div className="bg-danger-subtle p-3 rounded border border-danger">
                       <div className="d-flex justify-content-between mb-2">
                          <span className="small text-danger">Available Balance:</span>
                          <span className="fw-bold text-danger">{giftCardBalance.toFixed(2)} AED</span>
                       </div>
                       <FormGroup className="mb-2">
                          <Label className="small">Redemption Amount</Label>
                          <div className="d-flex gap-2">
                             <Input 
                                type="number" 
                                placeholder="Enter amount"
                                value={form.RedeemGiftCardAmount || ''}
                                onChange={e => {
                                   const val = parseFloat(e.target.value);
                                   if (val > giftCardBalance) return;
                                   setForm({...form, RedeemGiftCardAmount: e.target.value});
                                }}
                             />
                             <Button color="danger" onClick={confirmGiftCardRedemption}>
                                Apply
                             </Button>
                          </div>
                          <p className="text-muted small mt-1 mb-0">Max available: {giftCardBalance.toFixed(2)}</p>
                       </FormGroup>
                    </div>
                  )}
               </div>
            )}

            {/* Selection Display */}
            {(form.PointsAmount > 0 || form.GiftCardAmount > 0) && (
               <div className="mb-3 d-flex flex-wrap gap-2">
                  {form.PointsAmount > 0 && (
                     <div className="badge bg-primary-subtle text-primary p-2 d-flex align-items-center gap-2">
                        <span><i className="bx bx-award"></i> Loyalty: {form.PointsAmount.toFixed(2)} AED</span>
                        <i className="bx bx-x cursor-pointer" onClick={() => setForm({...form, PointsRedeemed: 0, PointsAmount: 0})}></i>
                     </div>
                  )}
                  {form.GiftCardAmount > 0 && (
                     <div className="badge bg-danger-subtle text-danger p-2 d-flex align-items-center gap-2">
                        <span><i className="bx bx-gift"></i> Gift Card: {form.GiftCardAmount.toFixed(2)} AED</span>
                        <i className="bx bx-x cursor-pointer" onClick={() => setForm({...form, GiftCardAmount: 0, GiftCardCode: ''})}></i>
                     </div>
                  )}
               </div>
            )}

            <div className="bg-light p-3 rounded mt-4">
               <div className="d-flex justify-content-between mb-2">
                  <span>Total Items</span>
                  <span className="fw-bold">{form.Invoice_TotalQty}</span>
               </div>
               {form.GiftCardAmount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-danger">
                     <span>Gift Card Discount</span>
                     <span>- {form.GiftCardAmount.toFixed(2)} AED</span>
                  </div>
               )}
               {form.PointsAmount > 0 && (
                  <div className="d-flex justify-content-between mb-2 text-primary">
                     <span>Loyalty Discount</span>
                     <span>- {form.PointsAmount.toFixed(2)} AED</span>
                  </div>
               )}
               <div className="d-flex justify-content-between">
                  <span>Payable Amount</span>
                  <span className="fw-bold text-primary">{(form.Invoice_NetAmount - (form.GiftCardAmount || 0) - (form.PointsAmount || 0)).toFixed(2)} AED</span>
               </div>
            </div>
         </ModalBody>
         <ModalFooter className="border-0">
            <Button color="light" className="px-4" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button 
               color="success" 
               className="px-5 fw-bold" 
               disabled={isSaving}
               onClick={() => handleSaveInvoice()}
            >
               {isSaving ? <Spinner size="sm" /> : 'Confirm & Print'}
            </Button>
         </ModalFooter>
      </Modal>

      <CompanyBranchModal
        isOpen={showCompanyModal}
        onSelectionComplete={handleCompanySelect}
        onCancel={() => {
           if (!selectedBusiness) navigate('/Dashboard');
           setShowCompanyModal(false);
        }}
      />
      {/* Invoice Print Modal */}
      {invoicePrintData && (
        <InvoicePrintModal
          isOpen={showInvoicePrintModal}
          toggle={handlePrintModalClose}
          invoiceData={invoicePrintData}
          businesses={businesses}
        />
      )}
    </React.Fragment>
  );
};

export default QuickInvoicePage;
